import { Inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { Wallet } from '@services/wallets/wallet.service';
import WalletConnect from '@walletconnect/client';
import { Core } from '@walletconnect/core';
import { Web3Wallet } from '@walletconnect/web3wallet';
import { ethers } from 'ethers';
import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';

import { sleep } from '@services/utils';
import { buildApprovedNamespaces, getSdkError } from '@walletconnect/utils';
import { ActionService } from '../action/action.service';
import { CryptoWalletService } from '../crypto-wallet/crypto-wallet.service';
import { EventTransportService } from '../event-transport/event-transport.service';
import { NetworksService } from '../networks/networks.service';
import { StorageService } from '../storage/storage.service';

export enum eWalletConnectEvent
{
    SESSION_REQUEST,
    SESSION_CLOSED,
    SESSION_CONNECTED,
    SESSION_CONNECT_FAIL,
}

export enum eState
{
    DISCONNECTED,
    CREATING_SESSION,
    WAIT_APPROVE,
    CONNECTING,
    CONNECTED,
}

// const CONNECT_TIMEOUT_MS = 20_000;
const CONNECT_TIMEOUT_MS = 5_000;

@Injectable({
    providedIn: 'root',
})
export class WalletConnectService
{
    private connector: any;
    private eventSubject: Subject<{ event: eWalletConnectEvent; data: any; }> =
        new Subject();

    private timeoutHandle: any;

    private state: eState;

    private wallet: Wallet;

    private core = new Core({
        projectId: "1bc55ee8882b2be736a93a2d28245443"
    })

    private web3Wallet;

    public topic: string;

    constructor(
        private networkService: NetworksService,
        @Inject(PLATFORM_ID) private platformId: any,
        private cryptoWalletService: CryptoWalletService,
        private router: Router,
        private injector: Injector,
        private eventTransport: EventTransportService,
        private storage: StorageService,
        private actionService: ActionService
    )
    {
        this.init();
    }

    private async init()
    {

        const web3Wallet = await Web3Wallet.init({
            core: this.core,
            metadata: {
                name: 'Cyberwallet',
                description: 'Cyberwallet',
                url: 'http://localgost:4200',
                icons: []
            }
        })

        if (Object.values(web3Wallet.getActiveSessions()).length === 0) {
            this.state = eState.DISCONNECTED;
        } else {
            this.state = eState.CONNECTED;
        }

        console.log(
            web3Wallet.getPendingSessionRequests(),
            web3Wallet.getPendingSessionProposals(),
            web3Wallet.getPendingAuthRequests(),
            web3Wallet.getActiveSessions()
        )

        if (web3Wallet.getPendingSessionProposals())
        {
            (web3Wallet.getPendingSessionProposals() as any).forEach(proposal =>
            {
                web3Wallet.rejectSession({
                    id: proposal.id,
                    reason: getSdkError('SESSION_SETTLEMENT_FAILED')
                })
            })
        }

        this.web3Wallet = web3Wallet;

        this.restoreSession();
    }

    public getConnector(): any
    {
        // return null;
        return this.connector;
    }

    public async connect(_uri: string): Promise<WalletConnect>
    {
        this.state = eState.CREATING_SESSION;

        this.timeoutHandle = setTimeout(() =>
        {
            this.state = eState.DISCONNECTED;

            this.eventSubject.next({
                event: eWalletConnectEvent.SESSION_CONNECT_FAIL,
                data: {},
            });
        }, CONNECT_TIMEOUT_MS);

        try
        {
            // this.connector = new WalletConnect({ uri: _uri });

            this.subscribeToEvents();
            await this.web3Wallet.core.pairing.pair({ uri: _uri });

            // if (!this.connector.connected)
            // {
            //     await this.connector.createSession();
            // }
        } catch (e)
        {
            if (Object.values(this.web3Wallet.getActiveSessions()).length === 0) {
                this.state = eState.DISCONNECTED;
            } else {
                this.state = eState.CONNECTED;
            }

            this.ClearConnectTimeout();
            throw e;
        }

        // return this.connector;
        return null;
    }

    public async approveSession(
        _propose: any,
        _address: string,
        _chainId: number
    ): Promise<WalletConnect>
    {
        try
        {
            console.log(_propose)
            console.log(_chainId)
            this.state = eState.CONNECTING;

            const blockchains = (await this.networkService.getBlockchains());
            // .filter(blockchain => blockchain.network);

            const chains = blockchains.map(blockchain => `eip155:${ blockchain.chain_id }`);
            const accounts = blockchains.map(blockchain => `eip155:${ blockchain.chain_id }:${ _address.toLowerCase() }`);

            // _propose.params.optionalNamespaces.eip155 = {
            //     chains: chains,
            //     events: [ "accountsChanged", "chainChanged", "message", "disconnect", "connect" ],
            //     methods: [ 'eth_sendTransaction', 'eth_signTypedData_v4', 'eth_signTypedData', 'personal_sign' ],
            // };

            const approvedNamespaces = buildApprovedNamespaces({
                proposal: _propose.params,
                supportedNamespaces: {
                    eip155: {
                        chains: chains,
                        methods: [ 'eth_sendTransaction', 'eth_signTypedData_v4', 'eth_signTypedData', 'personal_sign' ],
                        events: [ "accountsChanged", "chainChanged", "message", "disconnect", "connect" ],
                        accounts: accounts
                    }
                }
            });

            console.log(approvedNamespaces)

            const session = await this.web3Wallet.approveSession({
                id: _propose.id,
                namespaces: approvedNamespaces,
            });

            this.connector = session;

            this.init();

            this.wallet = (await this.cryptoWalletService.getWallets()).map(cw => cw.wallet).find(wallet => wallet.getAddress().toLowerCase() === _address.toLowerCase());

            this.state = eState.CONNECTED;

            this.eventSubject.next({
                event: eWalletConnectEvent.SESSION_CONNECTED,
                data: {},
            });

            return null;
        }
        catch (e)
        {
            console.error(e);

            if (Object.values(this.web3Wallet.getActiveSessions()).length === 0) {
                this.state = eState.DISCONNECTED;
            } else {
                this.state = eState.CONNECTED;
            }

            this.restoreSession();

            if (e.message.includes("namespaces chains don't satisfy required namespaces"))
            {
                this.web3Wallet.rejectSession({
                    id: _propose.id,
                    reason: getSdkError('UNSUPPORTED_CHAINS')
                })
            }

            this.eventSubject.next({
                event: eWalletConnectEvent.SESSION_CONNECT_FAIL,
                data: {},
            });

            throw e;
        }
    }

    public async rejectSession(_propose: any,)
    {

        await this.web3Wallet.rejectSession({
            id: _propose.id,
            reason: getSdkError('USER_REJECTED')
        })

        this.connector = null;
        return null;
    }

    public async killSession(index?: Record<string, unknown>)
    {
        if (this.connector)
        {
            await this.web3Wallet.disconnectSession({
                topic: index ? index.topic : this.connector.topic,
                reason: getSdkError('USER_REJECTED')
            });

            this.connector = null;
        }

        if (Object.values(this.web3Wallet.getActiveSessions()).length === 0) {
            this.state = eState.DISCONNECTED;
        } else {
            this.state = eState.CONNECTED;
        }

        this.restoreSession();
    }

    public GetState()
    {
        return this.state;
    }

    get events(): Observable<{ event: eWalletConnectEvent; data: any; }>
    {
        return this.eventSubject.asObservable();
    }

    public getWalletAddress()
    {
        return this.wallet?.address;
    }

    private subscribeToEvents()
    {
        this.web3Wallet.on('session_delete', ({ id, topic }) => {
            if (Object.values(this.web3Wallet.getActiveSessions()).length === 0) {
                this.state = eState.DISCONNECTED;
            } else {
                this.state = eState.CONNECTED;
            }

            this.restoreSession();

            if (this.router.url === '/more/wallet-connect') {
                return this.router.navigate(['/more']);
            }
        })
        this.web3Wallet.on('session_proposal', async proposal =>
        {
            console.log('session_proposal', proposal)
            if (this.state != eState.CREATING_SESSION)
            {
                console.log('dispatched obsoleted event');
                return;
            }

            this.ClearConnectTimeout();

            this.state = eState.WAIT_APPROVE;

            this.eventSubject.next({
                event: eWalletConnectEvent.SESSION_REQUEST,
                data: proposal,
            });
        })

        this.web3Wallet.on('session_request', async event =>
        {
            console.log(event)
            this.topic = event.topic;

            await sleep(1000);

            if (this.state != eState.CONNECTED)
            {
                console.log('dispatched obsoleted event');
                return;
            }

            const request = event.params.request;

            if (request.method === 'personal_sign')
            {
                await this.signMsg(
                    request.params[ 0 ],
                    request.params[ 1 ],
                    event.id,
                    '2.0'
                );
                return;
            }

            if (request.method === 'eth_signTypedData' || request.method === "eth_signTypedData_v4")
            {
                console.log('eth_signTypedData payload', request);
                await this.signMsg(
                    request.params[ 1 ],
                    request.params[ 0 ],
                    event.id,
                    '2.0',
                    'eth_signTypedData'
                );
                return;
            }

            const chain = event.params.chainId.match(/(.*):(.*)/)[ 2 ];

            const blockchains = (await this.networkService.getBlockchains());

            const blockchain = blockchains.find(blockchain => blockchain.chain_id === Number(chain));

            if (!blockchain.network)
            {
                this.eventTransport.send('toast', {
                    message: `Blockchain ${ blockchain.full_name } not enabled`,
                    type: 'error',
                    timeout: 0
                });
                const response = { id: event.id, jsonrpc: '2.0', error: { message: `Blockchain npt enabled` } };
                this.web3Wallet.respondSessionRequest({
                    topic: this.connector.topic,
                    response
                });
                return;
            }

            if (request.method === 'eth_sendTransaction')
            {
                this.sendTx(event.id, request.params[ 0 ], Number(chain));
            } else
            {
                const response = { id: event.id, jsonrpc: '2.0', error: { message: `No support ${ request.method }` } };
                this.web3Wallet.respondSessionRequest({
                    topic: this.connector.topic,
                    response
                });
            }
        })
    }

    private ClearConnectTimeout()
    {
        if (this.timeoutHandle != null)
        {
            clearTimeout(this.timeoutHandle);
            this.timeoutHandle = null;
        }
    }

    private async restoreSession()
    {
        const activeSessions = this.web3Wallet.getActiveSessions();

        if (Object.values(activeSessions))
        {
            this.connector = Object.values(activeSessions);

            for (const connectorItem of this.connector)
            {
                const account = connectorItem.namespaces.eip155.accounts[ 0 ].match(/(.*):(.*):(.*)/)[ 3 ];
                this.wallet = (await this.cryptoWalletService.getWallets())
                    .map(cw => cw.wallet)
                    .find(wallet => wallet.getAddress().toLowerCase() === account.toLowerCase());

                this.subscribeToEvents();
            }
        }
    }

    private async signMsg(data: string, walletAddress: string, id, jsonRPC, type: string = null)
    {
        let rawMsg, signType, displayMsg;
        if (type === "eth_signTypedData")
        {
            rawMsg = data;
            displayMsg = data;
            signType = 'eth_signTypedData_v4';
        }
        else
        {
            // const hexMsg = data.replace('0x', '');
            displayMsg = data;
            rawMsg = ethers.getBytes(data);
        }
        console.log("🚀 ~ file: wallet-connect.service.ts:399 ~ rawMsg", rawMsg);

        this.eventTransport.send('confirm-signMsg', {
            message: displayMsg,
            walletAddress: walletAddress,
        });

        const result = await this.eventTransport
            .on('signMsg-confirmation')
            .pipe(take(1))
            .toPromise();

        for (const connector of this.connector) {
            if (result.data.result)
            {
                this.wallet = (await this.cryptoWalletService.getWallets()).map(cw => cw.wallet).find(wallet => wallet.getAddress().toLowerCase() === walletAddress.toLowerCase());
                const signature = await this.wallet.signMsg(rawMsg, this.connector.chainId, signType);
                this.web3Wallet.respondSessionRequest({
                    topic: connector.topic,
                    response: {
                        id: id,
                        jsonrpc: jsonRPC,
                        result: signature,
                    }
                });
                this.eventTransport.send('sign-result', {
                    result: true,
                    description: 'success',
                });
            }
            else
            {
                const response = { id: id, jsonrpc: '2.0', error: { message: `User rejected` } };
                this.web3Wallet.respondSessionRequest({
                    topic: connector.topic,
                    response
                });
            }
        }
    }

    private async sendTx(_id: number, _tx: any, _chain: number)
    {
        const tx = _tx;

        const wallets = await this.cryptoWalletService.getWallets();
        const platform = this.networkService.getPlatformByChainId(_chain);

        const walletData = wallets.find((_data) =>
        {
            return (
                _data.wallet.getAddress().toLowerCase() ===
                _tx.from.toLowerCase() &&
                _data.wallet.getPlatform().toLowerCase() ===
                platform.toLowerCase()
            );
        });

        const isPrivateData = await walletData.wallet.checkPrivateDataExist();

        if (!isPrivateData)
        {
            const response = { id: _id, jsonrpc: '2.0', error: { message: `No wallet private key` } };
            this.web3Wallet.respondSessionRequest({
                topic: this.topic,
                response
            });

            return;
        }
        this.eventTransport.send('confirm-transaction', {
            tx,
            wallet: walletData.wallet,
            chainId: _chain
        });

        const result = await this.eventTransport
            .on('transaction-confirmation')
            .pipe(take(1))
            .toPromise();

        if (result.data.result === 'SUCCESS')
        {
            this.web3Wallet.respondSessionRequest({
                topic: this.topic,
                response: {
                    id: _id,
                    jsonrpc: '2.0',
                    result: result.data.hash,
                }
            });
        } else if (result.data.result === 'FAILED')
        {
            const response = { id: _id, jsonrpc: '2.0', error: { message: `Failed` } };
            this.web3Wallet.respondSessionRequest({
                topic: this.topic,
                response
            });
        } else
        {
            const response = { id: _id, jsonrpc: '2.0', error: { message: `User rejected`, code: "USER_REJECTED" } };
            this.web3Wallet.respondSessionRequest({
                topic: this.topic,
                response
            });
        }
    }
}
